import type { Container } from "@snap/ts-inject";
import { CONTAINER, Injectable } from "@snap/ts-inject";
import { firstValueFrom } from "rxjs";
import type { LensRepository } from "./lens/LensRepository";
import { lensRepositoryFactory } from "./lens/LensRepository";
import type { LensCoreError } from "./lens-core-module/lensCoreError";
import type { LensCore } from "./lens-core-module/lensCore";
import type { CameraKitSession } from "./session/CameraKitSession";
import { cameraKitSessionFactory } from "./session/CameraKitSession";
import { registerLensAssetsProvider } from "./lens/assets/LensAssetsProvider";
import { lensCoreFactory } from "./lens-core-module/loader/lensCoreFactory";
import { configurationToken, type Font } from "./configuration";
import type { RootServices } from "./RootServices";
import { registerUriHandlers } from "./uri-handlers/uriHandlersRegister";
import type { MetricsEventTarget } from "./metrics/metricsEventTarget";
import { metricsEventTargetFactory } from "./metrics/metricsEventTarget";
import { reportSessionScopedMetrics } from "./metrics/reporters/reporters";
import { lensStateFactory } from "./session/lensState";
import { lensKeyboardFactory } from "./session/LensKeyboard";
import { registerLensClientInterfaceHandler } from "./lens-client-interface/lensClientInterface";
import { sessionStateFactory } from "./session/sessionState";
import { lensExecutionError, lensAbortError, lensVideoPlaybackMutedError } from "./namedErrors";
import { getLogger, resetLogger } from "./logger/logger";
import { errorLoggingDecorator } from "./logger/errorLoggingDecorator";
import { TypedEventTarget } from "./events/TypedEventTarget";
import type { TypedCustomEvent } from "./events/TypedCustomEvent";
import type { LensView } from "./metrics/reporters/reportLensView";
import type { LensWait } from "./metrics/reporters/reportLensWait";
import type { PageVisibility } from "./common/pageVisibility";
import { pageVisibilityFactory } from "./common/pageVisibility";
import { setPreloadedConfiguration } from "./remote-configuration/preloadConfiguration";
import { registerConfigurationProvider } from "./remote-configuration/configurationProvider";
import type { RemoteConfiguration } from "./remote-configuration/remoteConfiguration";
import { getTrustedTypesHandler } from "./trusted-types/trustedTypesHandler";
import { remoteConfigurationFactory } from "./remote-configuration/remoteConfiguration";
import { registerGeoDataProvider } from "./geo/registerGeoDataProvider";
import { frameEventsFactory } from "./session/frameEvents";
import { registerLensAnalyticEventsHandler } from "./lens/analytics/registerLensAnalyticEventsHandler";

const logger = getLogger("CameraKit");

/**
 * Metrics event names that are exposed to apps.
 */
const publicMetricsEventNames: Array<LensMetricsEvents["detail"]["name"]> = ["lensView", "lensWait"];

/**
 * Lens metrics events.
 *
 * These events are emitted by {@link CameraKit} to report lens usage, performance, apply latency, etc.
 *
 * @category Lenses
 * @category Metrics
 */
export type LensMetricsEvents =
    | TypedCustomEvent<LensView["name"], LensView>
    | TypedCustomEvent<LensWait["name"], LensWait>;

/**
 * Options available when creating a {@link CameraKitSession}.
 *
 * @category Rendering
 */
export interface CreateSessionOptions {
    /**
     * Optionally provide an existing canvas element, on which the Live RenderTarget will be rendered.
     *
     * If this is not provided, CameraKit will create a new canvas element which can be added to the DOM.
     */
    liveRenderTarget?: HTMLCanvasElement;

    /**
     * Browsers optimize tabs when they are hidden - for example, by pausing the execution of requestAnimationFrame
     * callbacks.
     *
     * If you need the CameraKitSession to continue rendering even when the tab is in the background, set this to true.
     * There is a small performance penalty, and it's a good practice to only render in the background if absolutely
     * necessary.
     */
    renderWhileTabHidden?: boolean;
}

/**
 * The entry point to the CameraKit SDK's API. Most of CameraKit's features are accessed via this class.
 *
 * Applications obtain an instance of CameraKit by calling {@link bootstrapCameraKit}.
 *
 * @example
 * ```ts
 * const cameraKit = await bootstrapCameraKit(config)
 * ```
 *
 * Then this class can be used to:
 * - Create a {@link CameraKitSession} instance, which provides the API for setting up media inputs, applying Lenses,
 * and obtaining rendered `<canvas>` outputs.
 * - Query for lenses using {@link LensRepository}.
 * - Listen for lens usage metrics events using {@link MetricsEventTarget}.
 *
 * @category Rendering
 * @category Lenses
 */
export class CameraKit {
    /** @deprecated Use {@link lensRepository} */
    readonly lenses: { repository: LensRepository };

    /**
     * Business metrics (e.g. each time a lens is viewed) are emitted here.
     */
    readonly metrics: TypedEventTarget<LensMetricsEvents> = new TypedEventTarget();

    private sessions: CameraKitSession[] = [];

    /** @internal */
    constructor(
        /**
         * Used to query for lenses and lens groups.
         */
        readonly lensRepository: LensRepository,

        private readonly lensCore: LensCore,
        private readonly pageVisibility: PageVisibility,
        private readonly container: Container<RootServices>,
        private readonly remoteConfig: RemoteConfiguration,
        allMetrics: MetricsEventTarget
    ) {
        this.lenses = { repository: this.lensRepository };
        // Proxy only a subset of all metrics events to the public-facing emitter -- applications don't need to
        // know about most events.
        publicMetricsEventNames.forEach((eventName) => {
            allMetrics.addEventListener(eventName, (e) => this.metrics.dispatchEvent(e));
        });
    }

    /**
     * Create a CameraKitSession.
     *
     * This initializes the rendering engine and returns a {@link CameraKitSession} instance, which provides access
     * to Lens rendering.
     *
     * @example
     * ```ts
     * const cameraKit = await bootstrapCameraKit(config)
     * const session = await cameraKit.createSession()
     *
     * const lens = await cameraKit.lensRepository.loadLens(lensId, groupId)
     * session.applyLens(lens)
     * ```
     *
     * @param options
     */
    @errorLoggingDecorator(logger)
    async createSession({
        liveRenderTarget,
        renderWhileTabHidden,
    }: CreateSessionOptions = {}): Promise<CameraKitSession> {
        // Any error happened during lens rendering can be processed by subscribing to sessionErrors
        const exceptionHandler = (error: LensCoreError) => {
            if (error.name === "LensCoreAbortError") {
                logger.error(
                    lensAbortError(
                        "Unrecoverable error occurred during lens execution. " +
                            "The CameraKitSession will be destroyed.",
                        error
                    )
                );
            } else if (error.name === "LensCoreVideoPlaybackMutedError") {
                logger.error(
                    lensVideoPlaybackMutedError(
                        "Video playback was muted due to browser autoplay policies. " +
                            "Please prompt the user to interact with the page and unmute audio.",
                        error
                    )
                );
            } else if (error.name === "LensCoreVideoPlaybackFailedError") {
                logger.warn("Video playback failed.", error);
            } else {
                logger.error(
                    lensExecutionError(
                        "Error occurred during lens execution. " +
                            "The lens cannot be rendered and will be removed from the CameraKitSession.",
                        error
                    )
                );
            }
        };

        const config = this.container.get(configurationToken);

        /**
         * If/when we add support for multiple concurrent sessions, we'll need to create a copy of the LensCore WASM
         * module. If we move managing web workers into JS, spawning a new worker thread with its own copy of LensCore
         * probably becomes a lot more straightforward.
         *
         * Currently chromium has a bug preventing rendering while tab is hidden when LensCore is in worker mode.
         * In order to process tab while it is hidden, the current stopgap is to pass in renderWhileTabHidden as true,
         * which will initiate session in non worker mode, and set the RenderLoopMode to `SetTimeout`.
         */
        const trustedTypesHandler = getTrustedTypesHandler(config.trustedTypesPolicyName);
        await this.lensCore.initialize({
            canvas: liveRenderTarget,
            shouldUseWorker: !renderWhileTabHidden && config.shouldUseWorker,
            exceptionHandler,
            trustedTypes: {
                policyName: trustedTypesHandler.policyName,
                getTrustedUrls: trustedTypesHandler.getTrustedUrls,
                trustUrl: trustedTypesHandler.trustUrl,
            },
        });

        if (this.lensCore.setGpuIndex) {
            // NOTE: setGpuIndex was added in LensCore@292. This check ensures compatibility with older versions.

            // Camera Kit allows a performance cluster to be provided during bootstrap.
            // This cluster is passed to the LENS_FEATURE_GPU_INDEX config as LENS_CLUSTER_ORIG_4,
            // which maps the cluster to a GPU index.
            try {
                await this.lensCore.setGpuIndex({
                    gpuIndex: await firstValueFrom(this.remoteConfig.getGpuIndexConfig()),
                });
            } catch (cause) {
                // LensCore initialization can proceed without the GPU index,
                // as it is mainly needed for ML lenses.
                logger.error(new Error("Cannot set GPU index.", { cause }));
            }
        }

        if (config.fonts.length > 0) {
            // LensCore detaches font data and mutates font properties during processing.
            // To prevent issues when reusing font objects across multiple applications,
            // we create deep copies of font objects before passing them to LensCore.
            if (this.lensCore.setSystemFontFamilies) {
                this.lensCore.setSystemFontFamilies({
                    fontFamilies: config.fonts.map((font) =>
                        typeof font === "string"
                            ? [{ name: "Embedded", data: new ArrayBuffer(0) }]
                            : [{ ...font, data: font.data.slice(0) }]
                    ),
                });
            } else {
                // Fallback for older LensCore versions that don't support setSystemFontFamilies.
                // Filter out "default" entries since they are not supported by the legacy API.
                const fonts = config.fonts.filter((font): font is Font => typeof font !== "string");
                if (fonts.length > 0) {
                    this.lensCore.setSystemFonts({
                        fonts: fonts.map((font) => ({ ...font, data: font.data.slice(0) })),
                    });
                }
            }
        }

        await this.lensCore.setRenderLoopMode({
            mode: renderWhileTabHidden
                ? this.lensCore.RenderLoopMode.SetTimeout
                : this.lensCore.RenderLoopMode.RequestAnimationFrame,
        });

        // Each session gets its own DI Container – some Services provided by this Container may be shared with the
        // root CameraKit Container, but others may be scoped to the session by passing their token to `copy()`.
        const sessionContainer = this.container
            // Right now this is a no-op. If/when we add support for multiple concurrent sessions, we may end up
            // scoping LensCore to the session.
            .copy()

            .provides(sessionStateFactory)
            .provides(lensStateFactory)
            .provides(lensKeyboardFactory)
            .provides(frameEventsFactory)
            .provides(cameraKitSessionFactory)

            .run(registerLensAssetsProvider)
            .run(registerLensClientInterfaceHandler)
            .run(registerGeoDataProvider)
            .run(registerConfigurationProvider)
            .run(setPreloadedConfiguration)
            .run(registerLensAnalyticEventsHandler)

            // We'll run a PartialContainer containing reporters for session-scoped metrics. Running this container
            // allows each metric reporter to initialize itself (e.g. by adding event listeners to detect when certain
            // actions occur).
            .run(reportSessionScopedMetrics)

            // UriHandlers may have dependencies on session-scoped services (e.g. LensState, LensKeyboard), so they'll
            // be registered with LensCore here.
            .run(registerUriHandlers);

        const session = sessionContainer.get(cameraKitSessionFactory.token);
        this.sessions.push(session);
        return session;
    }

    /**
     * Destroys all sessions and frees all resources.
     */
    @errorLoggingDecorator(logger)
    async destroy() {
        resetLogger();
        this.pageVisibility.destroy();
        await Promise.all(this.sessions.map((session) => session.destroy()));
        this.sessions = [];
    }
}

/** @internal */
export const cameraKitFactory = Injectable(
    "CameraKit",
    [
        lensRepositoryFactory.token,
        metricsEventTargetFactory.token,
        lensCoreFactory.token,
        pageVisibilityFactory.token,
        remoteConfigurationFactory.token,
        CONTAINER,
    ] as const,
    (
        lensRepository: LensRepository,
        metrics: MetricsEventTarget,
        lensCore: LensCore,
        pageVisibility: PageVisibility,
        remoteConfiguration: RemoteConfiguration,
        container: Container<RootServices>
    ) => new CameraKit(lensRepository, lensCore, pageVisibility, container, remoteConfiguration, metrics)
);
